/** French strings for the action-plans slice (namespace: actionPlans.*). */
export const actionPlansFr = {
  title: "Plans d'action",
  subtitle: "Créez, structurez et suivez vos plans d'action.",
  loading: "Chargement…",

  list: {
    newPlan: "Nouveau plan",
    total: "Total",
    assessed: "complété",
    creating: "Création…",
    empty: "Aucun plan d'action pour l'instant.",
    emptyHint: "Créez un premier plan pour commencer à sélectionner des pistes d'action.",
    open: "Ouvrir",
    delete: "Supprimer",
    confirmDelete: "Supprimer définitivement ce plan d'action ?",
    untitled: "Plan sans nom",
    nameLabel: "Nom du plan",
    namePlaceholder: "Ex. : Plan d'action 2026",
    startYear: "Année de début",
    endYear: "Année de fin",
    selectedCount: "{{count}} pistes sélectionnées",
    created: "Créé le {{date}}",
    updated: "Mis à jour le {{date}}",
    inProgressCount: "{{count}} en cours",
    doneCount: "{{count}} réalisées",
    notDoneCount: "{{count}} non-réalisées",
    draft: "Brouillon",
  },

  steps: {
    issues: "Choix des enjeux",
    actions: "Pistes d'action",
    prioritization: "Priorisation",
    plan: "Planification",
    track: "Suivi",
  },
  settings: {
    edit: "Modifier le nom et la période",
    title: "Nom et période du plan",
  },
  tour: {
    replay: "Revoir le tutoriel",
    next: "Suivant",
    prev: "Précédent",
    done: "Compris !",
    progress: "{{current}} sur {{total}}",
    actions: {
      progressTitle: "Barre de progression",
      progressDesc:
        "Vous êtes à l'étape 2 sur 5 du plan d'action. Cette barre montre votre avancement.",
      consultTitle: "Consulter les parties prenantes",
      consultDesc:
        "Ce bouton vous permet de partager votre plan avec les catégories de parties prenantes pour recueillir leur avis.",
      issuesTitle: "Thématiques et enjeux",
      issuesDesc:
        "Vos pistes d'action sont organisées par thématique, puis par enjeu et objectif.",
      selectTitle: "Sélection des pistes d'action",
      selectDesc:
        "Cochez les pistes d'action que vous souhaitez intégrer dans votre plan d'action.",
      commentTitle: "Commentaires",
      commentDesc:
        "Cliquez sur cette icône pour consulter ou ajouter un commentaire à une piste d'action.",
      groupsTitle: "Catégorie de consultation",
      groupsDesc:
        "Assignez une ou plusieurs catégories de parties prenantes à consulter pour cette piste d'action.",
      addGoalTitle: "Ajouter un objectif",
      addGoalDesc:
        "Ajoutez un objectif personnalisé et ses pistes d'action pour cet enjeu.",
      goalGroupsTitle: "Catégories pour tout l'objectif",
      goalGroupsDesc:
        "Assignez des catégories de parties prenantes à toutes les pistes retenues de cet objectif d'un seul coup.",
      rewordGoalTitle: "Modifier un objectif",
      rewordGoalDesc:
        "Modifiez le nom de l'objectif et celui de ses pistes d'action, au même endroit.",
      nextTitle: "Navigation",
      nextDesc:
        "Quand vous avez terminé, et quand les parties prenantes auront répondu au sondage de priorisation, vous pourrez passer à l'étape 3 « Priorisation ».",
    },
    prioritization: {
      progressTitle: "Barre de progression",
      progressDesc:
        "Vous êtes à l'étape 3 sur 5 du plan d'action. Cette barre montre votre avancement.",
      consultationTitle: "Résultats de la consultation",
      consultationDesc:
        "Ce tableau résume les réponses de vos parties prenantes.",
      filtersTitle: "Filtres et recherche",
      filtersDesc:
        "Utilisez ces filtres pour afficher un sous-ensemble de pistes d'action par thématique, enjeu, score ou groupe.",
      autoSortTitle: "Tri automatique",
      autoSortDesc:
        "Ce bouton réordonne automatiquement les pistes d'action par score décroissant.",
      tableTitle: "Tableau de priorisation",
      tableDesc:
        "Vos pistes d'action triées par priorité. Cliquez sur le numéro pour saisir une position.",
      prePrioTitle: "Priorisation du groupe de travail",
      prePrioDesc:
        "Attribuez une note de 1 (basse priorité) à 5 (haute priorité) pour refléter la priorisation décidée par votre groupe de travail. Cette note s'ajoute aux scores des parties prenantes pour calculer le score total.",
      commentsTitle: "Commentaires",
      commentsDesc:
        "Cliquez sur la bulle pour consulter les commentaires laissés par les parties prenantes sur cette piste d'action.",
      nextTitle: "Navigation",
      nextDesc:
        "Quand vous avez terminé, passez à l'étape suivante ou revenez en arrière.",
    },
  },
  plan: {
    heading: "Planification des actions",
    help: "Bonifiez chaque piste d'action, s'il y a lieu, avec une date de début et d'échéance, un responsable, une cible et un coût estimé.",
    noSelected: "Sélectionnez d'abord des pistes d'action aux étapes précédentes.",
  },

  nav: {
    stepIndex: "Enjeux et objectifs du plan",
    backToList: "Retour à la liste",
    previousStep: "Étape précédente : {{step}}",
    nextStep: "Étape suivante : {{step}}",
  },

  issues: {
    heading: "Sélection des enjeux",
    help: "Choisissez les enjeux à travailler. Inclure un enjeu sélectionne toutes ses pistes d'action; vous pourrez les affiner à l'étape suivante.",
    included: "Inclus",
    included_count: "{{selected}} / {{total}} pistes",
    empty: "Ce plan n'a pas encore de structure d'enjeux.",
    emptyHint: "La structure provient de l'autoévaluation. Complétez le questionnaire pour alimenter ce plan.",
    selectAll: "Tout inclure",
    clearAll: "Tout retirer",
    selectShort: "tout",
    clearShort: "aucun",
    selectThematicAria: "Inclure tous les enjeux de la thématique {{thematic}}",
    clearThematicAria: "Retirer tous les enjeux de la thématique {{thematic}}",
    includedOf: "{{included}} / {{total}} inclus",
    colIssue: "Enjeu",
    colCompleted: "Réalisées",
    colGoals: "Obj.",
    colActions: "Pistes",
    completedActions_one: "{{count}} action réalisée",
    completedActions_other: "{{count}} actions réalisées",
    goalsCount_one: "{{count}} objectif",
    goalsCount_other: "{{count}} objectifs",
    availableActions_one: "{{count}} piste d'action disponible",
    availableActions_other: "{{count}} pistes d'action disponibles",
    resources: {
      experts_one: "{{count}} expert",
      experts_other: "{{count}} experts",
      tools_one: "{{count}} outil",
      tools_other: "{{count}} outils",
      aids_one: "{{count}} aide",
      aids_other: "{{count}} aides",
    },
    summaryTitle: "Ce plan",
    summaryIssues: "enjeux sur {{total}}",
    summaryActions: "pistes retenues sur {{total}}",
    summaryGoals: "objectifs couverts",
  },

  actions: {
    heading: "Choix des pistes d'action et consultation des parties prenantes",
    help: "Cochez les pistes d'action à inscrire, reformulez-les au besoin, ajoutez un commentaire et choisissez les groupes de parties prenantes à consulter. Lancez ensuite une consultation ci-dessous.",
    rewordAction: "Reformuler cette piste d'action",
    rewordActionLabel: "Formulation de la piste d'action",
    rewordGoal: "Reformuler cet objectif",
    rewordGoalLabel: "Nom de l'objectif",
    rewordHelp:
      "Au besoin, ajustez la formulation pour mieux refléter la réalité de vos processus. Laissez le champ vide pour conserver la formulation par défaut.",
    noActions: "Aucune piste d'action pour cet objectif.",
    extraBadge: "Boni",
    addAction: "Ajouter une piste d'action",
    addGoal: "Ajouter un objectif",
    addGoalHelp:
      "Nommez le nouvel objectif et ajoutez au moins une première piste d'action : un objectif ne peut exister sans piste d'action.",
    addGoalActionLabel: "Première piste d'action",
    goalNeedsAction: "Un objectif doit avoir au moins une action.",
    saving: "Enregistrement…",
    deleteAction: "Supprimer cette piste d'action",
    deleteGoal: "Supprimer cet objectif",
    confirmDeleteAction: "Supprimer cette piste d'action personnalisée ?",
    confirmDeleteGoal: "Supprimer cet objectif personnalisé et ses pistes d'action ?",
    addComment: "Ajouter un commentaire",
    viewComment: "Voir le commentaire",
    comment: "Commentaire",
    deleteComment: "Supprimer",
    commentPlaceholder: "Note facultative pour suivre la progression de cette piste…",
    save: "Enregistrer",
    cancel: "Annuler",
    selectGroups: "Choisir des groupes",
    groupsCount_one: "{{count}} groupe",
    groupsCount_other: "{{count}} groupes",
    noGroupsYet: "Créez des groupes de parties prenantes ci-dessous pour les assigner.",
    goalBadge: "Objectif",
    noIssues: "Aucun enjeu retenu pour ce plan.",
    noIssuesHint:
      "Revenez à l'étape « Choix des enjeux » pour choisir les enjeux à travailler; leurs pistes d'action apparaîtront ici.",
    extraGoalBadge: "Objectif ajouté",
    editObjective: "Modifier l'objectif et ses pistes d'action",
    editObjectiveHelp:
      "Reformulez l'objectif et ses pistes d'action. Videz un champ pour revenir au libellé d'origine.",
    editObjectiveActions: "Pistes d'action de cet objectif",
    goalGroupsTitle: "Catégories de parties prenantes (objectif)",
    goalGroupsAssign: "Assigner à l'objectif",
    goalGroupsMixed: "Mixte",
    goalGroupsNoSelection: "Aucune piste retenue",
    cascadeTitle: "Remplacer les catégories déjà choisies ?",
    cascadeBody:
      "Certaines pistes retenues ont déjà leurs propres catégories de parties prenantes. Appliquer le choix de l'objectif remplacera celles-ci sur les {{count}} pistes retenues.",
    cascadeConfirm: "Remplacer",
    goalsMissing_one: "{{count}} objectif sans piste retenue",
    goalsMissing_other: "{{count}} objectifs sans piste retenue",
    retainedOf: "{{selected}}/{{total}} retenues",
    retainedSummary:
      "{{selected}} pistes retenues sur {{total}} · {{assigned}} avec parties prenantes",
  },

  prioritization: {
    heading: "Priorisation",
    help: "Consultez les résultats de la consultation des parties prenantes et priorisez vos pistes d'action. Cette étape est facultative.",
    order: "Ordre de priorité",
    score: "Score",
    workingGroupPriority: "Priorisation du groupe de travail",
    consultationGroups: "Groupes consultés",
    consultationResults: "Résultats de la consultation",
    anonymised:
      "Les réponses individuelles sont anonymisées — seuls le score agrégé et les commentaires (sans nom) sont affichés.",
    group: "Catégorie",
    status: "Statut",
    responses: "Réponses",
    manage: "Actions",
    categoriesCount: "{{count}} catégorie(s)",
    actionColumn: "Piste d'action",
    open: "Ouverte",
    closed: "Fermée",
    openConsultation: "Ouvrir la consultation",
    closeConsultation: "Fermer la consultation",
    clearResponses: "Effacer les réponses",
    confirmClear:
      "Supprimer toutes les réponses de ce groupe ? Le groupe est conservé. Cette action est irréversible.",
    refreshScores: "Actualiser les scores",
    refreshed: "Scores actualisés.",
    search: "Rechercher…",
    autoSort: "Auto-trier par score",
    reword: "Reformuler",
    addToPlan: "Ajouter au plan d'action",
    confirmRemoveActionTitle: "Supprimer du plan d'action",
    confirmRemoveAction:
      "Supprimer cette piste d'action du plan ? Vous pourrez la rajouter tant que vous restez sur cette page, ou à l'étape précédente après l'avoir quittée.",
    removeOutOfFilter: "Supprimer les pistes hors filtre",
    bulkRemoveTitle: "Supprimer les pistes hors filtre",
    bulkRemoveIntro:
      "Ces pistes d'action sont masquées par le filtre actuel. Décochez celles à conserver, puis confirmez la suppression. Les pistes supprimées pourront être rajoutées à l'étape précédente.",
    bulkRemoveConfirm: "Supprimer {{count}} piste(s)",
    bulkRemoveButton: "Supprimer les pistes hors filtre ({{count}})",
    confirmRemove: "Supprimer du plan toutes les pistes masquées par le filtre actuel ?",
    filters: "Filtres",
    filterThematic: "Thématique",
    filterIssue: "Enjeu",
    filterGoal: "Objectif",
    filterAllF: "Toutes",
    filterAllM: "Tous",
    scoreMin: "Score min.",
    scoreMax: "Score max.",
    consulted: "Consulté",
    consultedYes: "Oui",
    consultedNo: "Non",
    stakeholderCategory: "Catégorie de parties prenantes",
    resetFilters: "Réinitialiser les filtres",
    tools: "Outils",
    removeFromPlan: "Supprimer du plan",
    stakeholderComments: "Commentaires des parties prenantes",
    ownComment: "Votre commentaire",
    stakeholderComments_one: "1 commentaire de partie prenante",
    stakeholderComments_other: "{{count}} commentaires de parties prenantes",
    notRated: "Pas encore évaluée",
    noSelected: "Sélectionnez d'abord des pistes à l'étape « Pistes d'action ».",
    noGroups:
      "Aucun groupe de consultation — assignez des groupes de parties prenantes à vos pistes à l'étape précédente.",
  },

  visualization: {
    heading: "Suivi du plan d'action",
    help: "Suivez l'avancement de chaque piste. Les modifications sont enregistrées automatiquement.",
    status: "Statut",
    responsible: "Responsable",
    responsiblePlaceholder: "Nom du responsable",
    startDate: "Date de début",
    deadline: "Échéance",
    estimatedCost: "Coût estimé ($)",
    estimatedCostHours: "Coût estimé (heures)",
    target: "Cible",
    targetPlaceholder: "Résultat visé",
    comment: "Commentaire",
    commentPlaceholder: "Notes et suivi…",
    noSelected: "Aucune piste sélectionnée à suivre.",
    downloadPdf: "Télécharger PDF",
    downloadExcel: "Télécharger Excel",
    print: "Version imprimable",
    history: "Historique",
    historyTitle: "Historique des modifications",
    exporting: "Exportation…",
    exportError: "L'export n'a pas pu être généré.",
    experts: "Experts",
    findAllExperts: "Consulter tous les experts pour cette piste d'action",
    findFavoriteExperts_one: "Expert favori ({{count}})",
    findFavoriteExperts_other: "Experts favoris ({{count}})",
    consultFavoriteExperts: "Consulter vos experts favoris pour cette piste d'action",
    noFavoriteExperts: "Aucun expert favori pour cette piste d'action",
    resources: "Ressources",
  },

  status: {
    not_done: "Non réalisé",
    in_progress: "En cours",
    done: "Réalisé",
  },

  groups: {
    heading: "Groupes de parties prenantes",
    help: "Gérez les groupes que vous consulterez sur vos pistes d'action.",
    name: "Nom du groupe",
    namePlaceholder: "Ex. : Employés, Clients…",
    introMessage: "Message d'introduction",
    add: "Ajouter le groupe",
    adding: "Ajout…",
    delete: "Supprimer",
    empty: "Aucune catégorie de parties prenantes créée.",
    optional: "Étape optionnelle",
    stepHelp:
      "Créez des catégories (ex. : employés, comité de direction) pour pouvoir les consulter à l'étape suivante sur les pistes d'action de votre choix. Les répondants proposeront une priorisation de 1 à 5 et pourront commenter.",
    rename: "Renommer la catégorie {{name}}",
    deleteNamed: "Supprimer la catégorie {{name}}",
    confirmDelete: "Supprimer la catégorie « {{name}} » ?",
  },

  consultations: {
    consultButton: "Consulter les parties prenantes",
    modalTitle: "Consultation des parties prenantes",
    modalHelp:
      "Chaque catégorie ayant au moins une piste d'action assignée dispose d'un lien de sondage (notation de 1 à 5). Partagez le lien ou envoyez l'invitation par courriel.",
    noGroups: "Aucune catégorie de parties prenantes.",
    newCategory: "Nouvelle catégorie",
    newCategoryPlaceholder: "Nom de la nouvelle catégorie…",
    addCategory: "Ajouter",
    assignToEnable:
      "Assignez des pistes d'action à cette catégorie pour activer son sondage.",
    timeEstimate: "{{count}} pistes · ~{{minutes}} min",
    responses: "{{count}} réponses",
    open: "Ouverte",
    closed: "Fermée",
    close: "Fermer",
    reopen: "Rouvrir",
    rename: "Renommer",
    cancel: "Annuler",
    save: "Enregistrer",
    copyLink: "Copier le lien",
    linkCopied: "Lien copié.",
    linkCopyFailed: "Impossible de copier le lien.",
    email: "Courriel d'invitation",
    emailLoading: "Chargement du modèle…",
    emailBody: "Message",
    recipients_label: "Destinataires",
    recipientsPlaceholder: "courriel1@exemple.com, courriel2@exemple.com",
    emailSave: "Enregistrer",
    emailSend: "Envoyer",
    emailSaved: "Modèle enregistré.",
    emailSent: "Courriel envoyé à {{count}} destinataire(s).",
  },

  errors: {
    loadFailed: "Échec du chargement.",
    retry: "Réessayer",
    saveFailed: "Échec de l'enregistrement.",
  },
  richText: {
    bold: "Gras",
    italic: "Italique",
    bulletList: "Liste à puces",
    numberedList: "Liste numérotée",
    link: "Lien",
    linkPrompt: "URL",
  },
} as const;
